import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Building2, Send, ShieldCheck, User } from 'lucide-react'
import { useEffect, useState } from 'react'
import { IMaskInput } from 'react-imask'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { PATHS } from '@/app/router/routes'
import useAuth from '@/features/auth/hooks/use-auth'
import { useFlowsQuery } from '@/features/flows/hooks/use-flows'
import { RenapPreviewCard } from '@/features/renap/components/renap-preview-card'
import { useAdvisorsQuery } from '@/features/users/hooks/use-users-query'
import { TypeAccount } from '@/features/users/models/user'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { FEATURES } from '@/shared/config/features'
import { queryKeys } from '@/shared/constants/query-keys'
import { EmailSuggestions } from '@/shared/form-sections/email-suggestions'
import { useErrorHandler } from '@/shared/hooks/use-error-handler'
import { cn } from '@/shared/lib/utils'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { clientErrorActions } from '../consts/client-error-actions'
import { startCreate } from '../coordinator/client-store-coordinator'
import { quickCreateClient, verifyQuickClient } from '../services/clients-admin.service'

type TypePerson = 'individual' | 'juridical'
type CreationMode = 'manual' | 'self'

const CUI_LENGTH = 13
const DPI_MASK = '0000 00000 0000'
const DPI_PLACEHOLDER = '____ _____ ____'

const maskedInputClassName =
  'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'

interface OptionCardProps {
  selected: boolean
  onClick: () => void
  icon: React.ReactNode
  title: string
  description: string
}

function OptionCard({ selected, onClick, icon, title, description }: OptionCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors',
        selected
          ? 'border-primary bg-primary/5 ring-1 ring-primary'
          : 'border-border hover:bg-muted/50'
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
          selected ? 'border-primary' : 'border-muted-foreground'
        )}
      >
        {selected && <span className="h-2 w-2 rounded-full bg-primary" />}
      </span>
      <span className="flex flex-col gap-0.5">
        <span className="flex items-center gap-1.5 text-sm font-medium">
          {icon}
          {title}
        </span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </span>
    </button>
  )
}

/** Rótulo de sub-grupo dentro de una sección (mayúsculas, atenuado). */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return <span className="text-xs font-medium uppercase text-muted-foreground">{children}</span>
}

interface AdvisorFieldProps {
  id: string
  // El valor enviado es el ObjectId (`_id`), no el `id` público (UUID legacy):
  // `Client.advisor` es un ref ObjectId y castear el UUID revienta con BSONError.
  // Espeja el patrón del alta interna (modal-create-client usa `dataProperties.id` = `_id`).
  advisors: Array<{ _id: string; fullname: string }>
  value: string
  onChange: (value: string) => void
  /**
   * Nombre de quien va a quedar como asesor SIN que se pueda elegir. Ver `imposedAdvisorName`
   * en el asistente: cuando quien crea es asesor, el servidor le asigna el cliente a sí mismo.
   */
  imposedName?: string | null
}

function AdvisorField({ id, advisors, value, onChange, imposedName }: AdvisorFieldProps) {
  /*
   * ☠️ UN ASESOR NO ELIGE ASESOR, Y EL SELECTOR LE MENTÍA.
   *
   * El servidor IMPONE el asesor cuando quien crea es uno: elegir a un compañero acá guardaba el
   * cliente a nombre propio igual, sin decir nada. Es el mismo desajuste que tenía el alta de
   * negociaciones, con una diferencia que lo hacía peor: por este camino —el puerto interno del
   * portal— la imposición ni siquiera existía hasta el 2026-08-14, así que la elección SÍ se
   * respetaba y un asesor podía dejarle el cliente a otro.
   *
   * Ahora se muestra a quién va a quedar asignado, que es lo que el servidor va a guardar.
   */
  if (imposedName) {
    return (
      <Field>
        <FieldLabel htmlFor={id}>Asesor</FieldLabel>
        <p id={id} className="text-sm">
          {imposedName}
        </p>
        <FieldDescription>El cliente queda asignado a tu cartera.</FieldDescription>
      </Field>
    )
  }

  return (
    <Field>
      <FieldLabel htmlFor={id}>
        Asesor <span className="font-normal text-muted-foreground">(opcional)</span>
      </FieldLabel>
      <NativeSelect
        id={id}
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
      >
        <NativeSelectOption value="" disabled>
          Selecciona una opción
        </NativeSelectOption>
        {advisors.map((a) => (
          <NativeSelectOption key={a._id} value={a._id}>
            {a.fullname}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </Field>
  )
}

interface ClientCreateWizardProps {
  onDone: () => void
}

/**
 * "Nuevo cliente" wizard (design SIDEBAR-2/3), montado dentro del side panel con
 * la estructura estándar (`SidePanelSection` + `SidePanelActions`), espejando el
 * panel de detalle del cliente.
 *
 * Step 1: person type + creation mode + advisor.
 *   - Manual → navigates to the existing internal creation form.
 *   - Self-service → step 2.
 * Step 2 (self-service): DPI (masked) o identidad manual.
 *   - Con DPI: "Verificar identidad" consulta RENAP y muestra un PASO de confirmación
 *     (foto + nombre + datos) para que el asesor confirme que es la persona correcta
 *     antes de crear y enviar —evita crear al cliente equivocado por un dígito mal. Si el
 *     CUI no existe, se avisa y no se crea nada. El backend re-verifica como red de seguridad.
 *   - Sin DPI: la identidad es manual, se crea y envía directo (sin confirmación).
 */
export function ClientCreateWizard({ onDone }: ClientCreateWizardProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Asesores desde su endpoint dedicado (`GET /users/advisor`), no desde el lookup de
  // usuarios: el portón de aquél (`LOOKUP_ACCESS.advisors`) lo abre cualquier permiso de
  // clientes, mientras que el de usuarios sólo lo abren ReadUsers, roles y proyectos —
  // ninguno de los cuales tiene quien da de alta clientes. El alta le fallaba con
  // «At least one of the following is required: ReadUsers, CreateRoles, …».
  const { data: advisors = [] } = useAdvisorsQuery()
  // Actionable errors (e.g. duplicate client → "Ver cliente") via the shared system.
  const handleError = useErrorHandler(clientErrorActions)

  const { auth } = useAuth()

  const [step, setStep] = useState<1 | 2>(1)
  const [typePerson, setTypePerson] = useState<TypePerson>('individual')
  const [mode, setMode] = useState<CreationMode>('manual')
  const [advisorId, setAdvisorId] = useState('')

  /**
   * A quién le va a quedar el cliente cuando no hay nada que elegir.
   *
   * El servidor impone el asesor si quien crea es uno, así que mostrarle un selector es ofrecerle
   * una decisión que no tiene. `null` para todos los demás, que sí eligen.
   */
  const imposedAdvisorName =
    auth?.typeAccount === TypeAccount.advisor ? (auth?.fullname ?? 'Tu cartera') : null

  /**
   * La autogestión ES el portal: crea el cliente Y le manda el formulario, y su
   * `flowDefinitionId` es obligatorio para poder guardar. Con el portal apagado no se ofrece.
   *
   * El **llenado manual** no depende del portal —navega al FEIC— así que el asistente entero
   * sigue sirviendo. Ésa es la razón por la que el gate va acá adentro y no sobre el botón que
   * abre este panel: gatear la puerta dejaba al equipo sin ninguna forma de dar de alta un
   * cliente desde la interfaz, porque las rutas del FEIC no tienen ningún enlace que las abra.
   */
  const selfServiceAvailable = FEATURES.clientPortal

  // Step 2
  // ¿El cliente/representante tiene DPI? Decide qué campos de identidad se piden: con DPI
  // los datos vienen de RENAP (no se pide nombre/apellido); sin DPI se capturan a mano.
  // `null` = aún no eligió → no se muestran campos de identidad (evita el formulario
  // saturado donde DPI + nombre + apellido aparecían todos juntos).
  const [hasDpi, setHasDpi] = useState<boolean | null>(null)
  const [dpi, setDpi] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [email, setEmail] = useState('')
  // Validación on-blur (no on-mount): los errores de identidad/empresa sólo se muestran
  // una vez que el usuario tocó el campo. Evita que "Ingresa el nombre…" aparezca de una
  // apenas se elige "No tiene DPI".
  const [nameTouched, setNameTouched] = useState(false)
  const [companyTouched, setCompanyTouched] = useState(false)
  // Paso de confirmación: tras verificar el DPI en RENAP mostramos la identidad para que el
  // asesor confirme que es la persona correcta ANTES de crear y enviar.
  const [confirming, setConfirming] = useState(false)
  // Sólo los flujos del tipo de persona elegido (+ los 'both').
  const { data: flows = [] } = useFlowsQuery('client_portal', typePerson)
  // Alta de cliente: SÓLO flujos del contexto de creación (los de cliente existente /
  // revalidación no aplican al crear).
  const activeFlows = flows.filter((f) => f.active && f.context === 'client_creation')
  const [flowDefinitionId, setFlowDefinitionId] = useState('')

  // Preselecciona el flujo de alta por defecto; re-selecciona si el elegido ya no aplica
  // al tipo de persona actual (al cambiar individual ↔ jurídica).
  useEffect(() => {
    if (activeFlows.length === 0) return
    if (!activeFlows.some((f) => f.id === flowDefinitionId)) {
      const preferred = activeFlows.find((f) => f.isDefault) ?? activeFlows[0]
      setFlowDefinitionId(preferred.id)
    }
  }, [activeFlows, flowDefinitionId])

  const isJuridical = typePerson === 'juridical'

  const cuiDigits = dpi.replace(/\D/g, '')

  // Identidad completa según la rama elegida: con DPI exige el CUI de 13 dígitos (el
  // backend resuelve el nombre vía RENAP); sin DPI exige nombre o apellido a mano. Hasta
  // que elija una rama, la identidad está incompleta.
  const dpiIncomplete = hasDpi === true && cuiDigits.length !== CUI_LENGTH
  const manualNameMissing = hasDpi === false && !firstName.trim() && !lastName.trim()
  const identityIncomplete = hasDpi === null || dpiIncomplete || manualNameMissing
  // Juridical additionally requires the company's razón social (the client is the company).
  const companyNameMissing = isJuridical && !companyName.trim()
  const canSubmit =
    !!email.trim() && !identityIncomplete && !companyNameMissing && !!flowDefinitionId
  // El error inline sólo tras interacción (on-blur), aunque la validación de canSubmit
  // ya deshabilite el botón desde el principio.
  const showManualNameError = manualNameMissing && nameTouched
  const showCompanyError = companyNameMissing && companyTouched

  const createMutation = useMutation({
    mutationFn: async () =>
      // Con DPI la identidad la resuelve RENAP en el backend con el CUI (no se envían
      // nombres). Sin DPI, se mandan los nombres capturados a mano.
      await quickCreateClient({
        clientType: typePerson,
        firstName: hasDpi ? '' : firstName,
        lastName: hasDpi ? '' : lastName,
        // Razón social: sólo aplica a la persona jurídica (identifica al cliente-empresa).
        companyName: isJuridical ? companyName.trim() : undefined,
        // El DPI sólo se envía en la rama "con DPI".
        identificationNumber: hasDpi ? cuiDigits : undefined,
        identificationType: 'D',
        clientEmail: email,
        advisorId: advisorId || undefined,
        flowDefinitionId
      }),
    onSuccess: () => {
      toast.success('Cliente creado y formulario enviado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.clients.all })
      onDone()
    },
    // Rich, actionable error toast (duplicate → "Ver cliente"; DPI inexistente en RENAP →
    // mensaje del backend); plain message + Sentry para el resto.
    onError: handleError
  })

  // Verifica el DPI ANTES de crear: el backend rechaza si ya es cliente (CLIENT_DUPLICATE →
  // "Ver cliente") y sólo si no existe consulta RENAP. Con la identidad, pasa a confirmar.
  const verifyMutation = useMutation({
    mutationFn: async () =>
      await verifyQuickClient({
        clientType: typePerson,
        identificationNumber: cuiDigits,
        identificationType: 'D'
      }),
    onSuccess: () => {
      setConfirming(true)
    },
    onError: handleError
  })

  /** Cambia la rama de identidad y limpia los datos de la rama opuesta (evita arrastres). */
  const selectHasDpi = (value: boolean) => {
    setHasDpi(value)
    setNameTouched(false)
    setConfirming(false)
    if (value) {
      setFirstName('')
      setLastName('')
    } else {
      setDpi('')
    }
  }

  const goManual = () => {
    // Initialize the client stores before navigating: the internal form (FEIC)
    // only mounts when the coordinator has seeded the create state. Skipping this
    // was why the form stayed blank when reached from the wizard.
    startCreate({ typePerson, advisorId })
    onDone()
    // Absolute path: this wizard renders inside a side panel, so a relative '../'
    // resolved against the wrong route base (e.g. /individual/create).
    navigate(
      typePerson === 'individual'
        ? PATHS.admin.clients.individual.create
        : PATHS.admin.clients.juridical.create
    )
  }

  const handleStep1 = () => {
    if (mode === 'manual') {
      goManual()
      return
    }
    setStep(2)
  }

  if (step === 1) {
    return (
      <div className="flex flex-col">
        <SidePanelSection>
          <SidePanelSectionBody className="gap-2">
            <SectionLabel>Selecciona el tipo de persona</SectionLabel>
            <OptionCard
              selected={typePerson === 'individual'}
              onClick={() => {
                setTypePerson('individual')
              }}
              icon={<User className="h-3.5 w-3.5" />}
              title="Persona Individual"
              description="Para clientes que operan a título personal"
            />
            <OptionCard
              selected={typePerson === 'juridical'}
              onClick={() => {
                setTypePerson('juridical')
              }}
              icon={<Building2 className="h-3.5 w-3.5" />}
              title="Persona Jurídica"
              description="Para clientes que operan bajo una entidad jurídica"
            />
          </SidePanelSectionBody>
        </SidePanelSection>

        {/* Con una sola opción disponible no hay nada que elegir: el paso se omite entero. */}
        {selfServiceAvailable ? (
          <SidePanelSection>
            <SidePanelSectionBody className="gap-2">
              <SectionLabel>Modo de creación</SectionLabel>
              <OptionCard
                selected={mode === 'manual'}
                onClick={() => {
                  setMode('manual')
                }}
                icon={null}
                title="Llenado manual"
                description="El equipo recopila y registra la información"
              />
              <OptionCard
                selected={mode === 'self'}
                onClick={() => {
                  setMode('self')
                }}
                icon={null}
                title="Autogestión del cliente"
                description="El cliente se encarga de completar el registro"
              />
            </SidePanelSectionBody>
          </SidePanelSection>
        ) : null}

        <SidePanelSection>
          <AdvisorField
            id="advisor"
            advisors={advisors}
            value={advisorId}
            onChange={setAdvisorId}
            imposedName={imposedAdvisorName}
          />
        </SidePanelSection>

        <SidePanelSection separated={false}>
          <SidePanelActions
            actions={[
              {
                label: mode === 'manual' ? 'Crear cliente' : 'Continuar',
                primary: true,
                onClick: handleStep1
              }
            ]}
          />
        </SidePanelSection>
      </div>
    )
  }

  // Paso de confirmación (rama con DPI): identidad verificada en RENAP + resumen del envío.
  if (confirming && verifyMutation.data) {
    const identity = verifyMutation.data
    const selectedFlowName = activeFlows.find((f) => f.id === flowDefinitionId)?.name ?? ''
    const formattedDpi = cuiDigits.replace(/(\d{4})(\d{5})(\d{4})/, '$1 $2 $3')

    return (
      <div className="flex flex-col">
        <SidePanelSection>
          <SidePanelSectionBody className="gap-3">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-green-700">
              <ShieldCheck className="h-4 w-4" />
              Identidad verificada en RENAP
            </span>
            <div className="rounded-lg border border-green-200 bg-green-50/60 p-3">
              <RenapPreviewCard applicant={identity.applicant} photo={identity.photo} />
            </div>

            <dl className="flex flex-col gap-1.5 text-xs">
              {isJuridical && (
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Empresa</dt>
                  <dd className="text-right font-medium">{companyName.trim()}</dd>
                </div>
              )}
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">DPI</dt>
                <dd className="text-right font-medium tabular-nums">{formattedDpi}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Correo</dt>
                <dd className="text-right font-medium break-all">{email.trim()}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Validación</dt>
                <dd className="text-right font-medium">{selectedFlowName}</dd>
              </div>
            </dl>

            <p className="text-sm text-muted-foreground">
              {isJuridical
                ? '¿Es este el representante legal correcto? Se creará el cliente y se le enviará el formulario.'
                : '¿Es esta la persona correcta? Se creará el cliente y se le enviará el formulario.'}
            </p>
          </SidePanelSectionBody>
        </SidePanelSection>

        <SidePanelSection separated={false}>
          <SidePanelActions
            actions={[
              {
                label: 'Volver a editar',
                variant: 'ghost',
                icon: <ArrowLeft className="h-4 w-4" />,
                onClick: () => {
                  setConfirming(false)
                }
              },
              {
                label: createMutation.isPending ? 'Enviando…' : 'Confirmar y enviar',
                primary: true,
                icon: <Send className="h-4 w-4" />,
                disabled: createMutation.isPending,
                onClick: () => {
                  createMutation.mutate()
                }
              }
            ]}
          />
        </SidePanelSection>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      {/* Persona jurídica: el cliente es la EMPRESA (razón social). Quien gestiona
          el formulario es su REPRESENTANTE LEGAL —una persona real, RENAP-válida—,
          cuya identidad y correo se capturan en el bloque siguiente. */}
      {isJuridical && (
        <SidePanelSection>
          <SidePanelSectionBody className="gap-2">
            <span className="text-sm font-semibold text-primary">Datos de la empresa</span>
            <Field data-invalid={showCompanyError}>
              <FieldLabel htmlFor="companyName">Nombre de la empresa</FieldLabel>
              <Input
                id="companyName"
                value={companyName}
                placeholder="Razón social"
                onChange={(e) => {
                  setCompanyName(e.target.value)
                }}
                onBlur={() => {
                  setCompanyTouched(true)
                }}
                aria-invalid={showCompanyError || undefined}
                aria-required
              />
              {showCompanyError ? (
                <FieldError errors={[{ message: 'Ingresa la razón social de la empresa.' }]} />
              ) : (
                <FieldDescription>Razón social tal como aparece en la patente.</FieldDescription>
              )}
            </Field>
          </SidePanelSectionBody>
        </SidePanelSection>
      )}

      <SidePanelSection>
        <SidePanelSectionBody className="gap-3">
          <span className="text-sm font-semibold text-primary">
            {isJuridical ? 'Datos del representante legal' : 'Datos del cliente'}
          </span>

          {/* Rama de identidad: con DPI los datos vienen de RENAP; sin DPI se piden a
              mano. El selector evita mostrar todos los campos a la vez (no aplican todos). */}
          <div className="flex flex-col gap-2">
            <SectionLabel>
              {isJuridical ? '¿El representante tiene DPI?' : '¿El cliente tiene DPI?'}
            </SectionLabel>
            <OptionCard
              selected={hasDpi === true}
              onClick={() => {
                selectHasDpi(true)
              }}
              icon={null}
              title="Sí, tiene DPI"
              description="Obtenemos su identidad desde RENAP con el número de DPI."
            />
            <OptionCard
              selected={hasDpi === false}
              onClick={() => {
                selectHasDpi(false)
              }}
              icon={null}
              title="No tiene DPI"
              description="Se registra el nombre y apellido manualmente."
            />
          </div>

          {/* Con DPI: sólo el número (máscara). La verificación RENAP corre en el
              backend al "Crear y enviar" —sin botón de autocompletar. */}
          {hasDpi === true && (
            <Field>
              <FieldLabel htmlFor="dpi">Número de DPI</FieldLabel>
              <IMaskInput
                id="dpi"
                mask={DPI_MASK}
                unmask
                lazy={false}
                placeholder={DPI_PLACEHOLDER}
                inputMode="numeric"
                autoComplete="off"
                value={dpi}
                onAccept={(value: string) => {
                  setDpi(value)
                }}
                className={maskedInputClassName}
              />
              <FieldDescription>
                Verificamos el DPI en RENAP y te mostramos la identidad para confirmar antes de
                crear.
              </FieldDescription>
            </Field>
          )}

          {/* Sin DPI: la identidad se desconoce, se captura nombre o apellido. */}
          {hasDpi === false && (
            <>
              <Field>
                <FieldLabel htmlFor="firstName">Nombre</FieldLabel>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value)
                  }}
                  onBlur={() => {
                    setNameTouched(true)
                  }}
                />
              </Field>

              <Field data-invalid={showManualNameError}>
                <FieldLabel htmlFor="lastName">Apellido</FieldLabel>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value)
                  }}
                  onBlur={() => {
                    setNameTouched(true)
                  }}
                  aria-invalid={showManualNameError || undefined}
                />
                {showManualNameError ? (
                  <FieldError errors={[{ message: 'Ingresa el nombre o el apellido.' }]} />
                ) : (
                  <FieldDescription>Ingresa al menos el nombre o el apellido.</FieldDescription>
                )}
              </Field>
            </>
          )}
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelSectionBody className="gap-3">
          <Field>
            <FieldLabel htmlFor="email">Correo electrónico</FieldLabel>
            <Input
              id="email"
              type="email"
              inputMode="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
              }}
              aria-required
            />
            <EmailSuggestions value={email} onSelect={setEmail} />
            {isJuridical && (
              <FieldDescription>
                A este correo del representante se le envía el enlace del formulario.
              </FieldDescription>
            )}
          </Field>

          <Field>
            <FieldLabel htmlFor="flowDefinition">Tipo de validación</FieldLabel>
            <NativeSelect
              id="flowDefinition"
              value={flowDefinitionId}
              onChange={(e) => {
                setFlowDefinitionId(e.target.value)
              }}
            >
              {activeFlows.length === 0 ? (
                <NativeSelectOption value="" disabled>
                  No hay flujos configurados
                </NativeSelectOption>
              ) : (
                activeFlows.map((flow) => (
                  <NativeSelectOption key={flow.id} value={flow.id}>
                    {flow.name}
                  </NativeSelectOption>
                ))
              )}
            </NativeSelect>
          </Field>

          <AdvisorField
            id="advisor2"
            advisors={advisors}
            value={advisorId}
            onChange={setAdvisorId}
            imposedName={imposedAdvisorName}
          />
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection separated={false}>
        <SidePanelActions
          actions={[
            {
              label: 'Regresar',
              variant: 'ghost',
              icon: <ArrowLeft className="h-4 w-4" />,
              onClick: () => {
                setStep(1)
              }
            },
            // Con DPI: verificar identidad en RENAP y confirmar antes de crear. Sin DPI:
            // la identidad es manual, se crea y envía directo.
            hasDpi === true
              ? {
                  label: verifyMutation.isPending ? 'Verificando…' : 'Verificar identidad',
                  primary: true,
                  icon: <ShieldCheck className="h-4 w-4" />,
                  disabled: verifyMutation.isPending || !canSubmit,
                  onClick: () => {
                    verifyMutation.mutate()
                  }
                }
              : {
                  label: createMutation.isPending ? 'Creando…' : 'Crear y enviar',
                  primary: true,
                  icon: <Send className="h-4 w-4" />,
                  disabled: createMutation.isPending || !canSubmit,
                  onClick: () => {
                    createMutation.mutate()
                  }
                }
          ]}
        />
      </SidePanelSection>
    </div>
  )
}
